import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import farmsConfig from 'config/constants/farms'
import isArchivedPid from 'utils/farmHelpers'
import priceHelperLpsConfig from 'config/constants/priceHelperLps'
import { getMasterChefAddress } from 'utils/addressHelpers'
import masterchefABI from 'config/abi/ZenMaster.json'
import multicall from 'utils/multicall'
import fetchFarms from './fetchFarms'
import fetchFarmsPrices from './fetchFarmsPrices'
import fetchFarmsRewardsData from './fetchFarmsRewardsData'
import {
  fetchFarmUserEarnings,
  fetchFarmUserAllowances,
  fetchFarmUserTokenBalances,
  fetchFarmUserStakedBalances,
  fetchSlaveFarmUserData,
} from './fetchFarmUser'
import { FarmsState, Farm } from '../types'


const noAccountFarmConfig = farmsConfig.map((farm) => ({
  ...farm,
  userData: {
    allowance: '0',
    tokenBalance: '0',
    stakedBalance: '0',
    earnings: '0',
    slaveUserData: [],
  },
}))

const initialState: FarmsState = { 
  data: noAccountFarmConfig, 
  loadArchivedFarmsData: false, 
  userDataLoaded: false 
}

export const nonArchivedFarms = farmsConfig.filter(({ pid }) => !isArchivedPid(pid))

// Async thunks
export const fetchFarmsPublicDataAsync = createAsyncThunk<Farm[], number[]>(
  'farms/fetchFarmsPublicDataAsync',
  async (pids) => {
    const farmsToFetch = farmsConfig.filter((farmConfig) => pids.includes(farmConfig.pid))

    // Add price helper farms
    const farmsWithPriceHelpers = farmsToFetch.concat(priceHelperLpsConfig)
    const farms = await fetchFarms(farmsWithPriceHelpers)
    const farmsWithRewardsData = await fetchFarmsRewardsData(farms)
    const farmsWithPrices = await fetchFarmsPrices(farmsWithRewardsData)

    // Filter out price helper LP config farms
    const farmsWithoutHelperLps = farmsWithPrices.filter((farm: Farm) => {
      return farm.pid || farm.pid === 0
    })

    return farmsWithoutHelperLps
  },
)

interface SlaveFarmUserData {
  slaveFarmAddress: string
  stakedBalance: string
  earnings: string  
}

interface FarmUserDataResponse {
  pid: number
  allowance: string
  tokenBalance: string
  stakedBalance: string
  earnings: string
  slaveUserData: SlaveFarmUserData[]
}

const fetchSlaveFarmAddresses = async (): Promise<string[]> => {
  const FarmMasterAddress = getMasterChefAddress()
  const call = {
    address: FarmMasterAddress,
    name: 'getSlaveFarms', // Ensure the contract has this method or adjust accordingly
  }

  const [addresses] = await multicall(masterchefABI, [call])
  return addresses[0]
}

export const fetchFarmUserDataAsync = createAsyncThunk<FarmUserDataResponse[], { account: string; pids: number[] }>(
  'farms/fetchFarmUserDataAsync',
  async ({ account, pids }) => {
    const farmsToFetch = farmsConfig.filter((farmConfig) => pids.includes(farmConfig.pid))
    const slaveFarmAddresses = await fetchSlaveFarmAddresses()
    const userFarmAllowances = await fetchFarmUserAllowances(account, farmsToFetch)
    const userFarmTokenBalances = await fetchFarmUserTokenBalances(account, farmsToFetch)
    const userStakedBalances = await fetchFarmUserStakedBalances(account, farmsToFetch)
    const userFarmEarnings = await fetchFarmUserEarnings(account, farmsToFetch)

    // Process each farm and gather its user data along with its slave farms data
    const farmUserDataResponses = await Promise.all(
      farmsToFetch.map(async (farmConfig, index) => {        
        const pid = farmConfig.pid
        const slaveFarmUserData = await fetchSlaveFarmUserData(pid, account, slaveFarmAddresses)

       // Create the slaveUserData array for this farm
       const slaveUserData = slaveFarmAddresses.map((address) => {
        const userData = slaveFarmUserData.find((data) => data.slaveFarmAddress === address);
        return {
          slaveFarmAddress: address,
          stakedBalance: userData ? userData.stakedBalance : "0",
          earnings: userData ? userData.earnings : "0",
        };
      });

      // Return the extended farm data including slave farms
      return {
        pid,
        allowance: userFarmAllowances[index],
        tokenBalance: userFarmTokenBalances[index],
        stakedBalance: userStakedBalances[index],
        earnings: userFarmEarnings[index],
        slaveUserData, // Attach slave farm data
      };
    })
  );

  // Map farm user allowances to FarmUserDataResponse format
  return farmUserDataResponses;
}
);


export const farmsSlice = createSlice({
  name: 'Farms',
  initialState,
  reducers: {
    setLoadArchivedFarmsData: (state, action) => {
      const loadArchivedFarmsData = action.payload
      state.loadArchivedFarmsData = loadArchivedFarmsData
    },
  },
  extraReducers: (builder) => {
    // Update farms with live data
    builder.addCase(fetchFarmsPublicDataAsync.fulfilled, (state, action) => {
      state.data = state.data.map((farm) => {
        const liveFarmData = action.payload.find((farmData) => farmData.pid === farm.pid)
        return { ...farm, ...liveFarmData }
      })
    })

    // Update farms with user data
    builder.addCase(fetchFarmUserDataAsync.fulfilled, (state, action) => {
      action.payload.forEach((userDataEl) => {
        const { pid } = userDataEl
        const index = state.data.findIndex((farm) => farm.pid === pid)
        state.data[index] = { ...state.data[index], userData: userDataEl }
      })
      state.userDataLoaded = true
    })
  },
})

// Actions
export const { setLoadArchivedFarmsData } = farmsSlice.actions

export default farmsSlice.reducer
